import { useQuery } from '@tanstack/react-query'
import { clientsAPI } from 'api/clientsAPI'
import { platformAPI } from 'api/platformAPI'
import { useState } from 'react'

export default function useClient(
  clientId?: number,
  getInvoiceSettings = false,
  getTravelPolicies = false,
  getPlatformSettings = false,
) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const updateClient = async (data: any, id: number) => {
    setIsLoading(true)
    setError(false)

    try {
      const resp = await clientsAPI.updateClient(data, id)

      if (resp && resp.data?.error) {
        setError(true)
      }
    } catch (error) {
      setError(true)
    }

    setIsLoading(false)
  }

  const updateClientInvoicing = async (
    data: any,
    id: number,
    create: boolean,
  ) => {
    setIsLoading(true)
    setError(false)

    try {
      const resp = create
        ? await clientsAPI.updateInvoiceSettings(id, data)
        : await clientsAPI.createInvoiceSettings(id, data)

      if (resp && resp.data?.error) {
        setError(true)
      }
    } catch (error) {
      setError(true)
    }

    setIsLoading(false)
  }

  const fallbackResult = {
    client: undefined,
    refetch: async () => undefined,
    fixedPriceDisplay: false,
    displayVATPrice: false,
    clientThemeName: false,
    isLoading,
    error,
    updateClientInvoicing,
    updateClient,
  }

  try {
    const query = useQuery({
      queryKey: ['clients', clientId, getInvoiceSettings, getTravelPolicies],
      queryFn: async () => {
        const response = await clientsAPI.getClient(clientId)
        if (response) {
          const [
            invoiceSettings,
            travelPolicies,
            platformSettings,
            parentClient,
          ] = await Promise.all([
            getInvoiceSettings
              ? clientsAPI.getInvoiceSettings(clientId).catch(() => null)
              : Promise.resolve(null),
            getTravelPolicies
              ? clientsAPI
                  .getClientTravelPolicies(clientId)
                  .then((r) => r.value)
                  .catch(() => null)
              : Promise.resolve(null),
            getPlatformSettings
              ? platformAPI
                  .getPlatform(response.platformId)
                  .then((r) => r)
                  .catch(() => null)
              : Promise.resolve(null),
            response.parentClientId > 0
              ? clientsAPI.getClient(response.parentClientId).catch(() => null)
              : Promise.resolve(null),
          ])

          if (getInvoiceSettings) response.invoiceSettings = invoiceSettings
          if (getTravelPolicies) response.travelPolicies = travelPolicies
          if (response.parentClientId > 0) response.parentClient = parentClient
          response.platformSettings = platformSettings

          return response
        } else {
          throw new Error('Unable to get client')
        }
      },
      enabled: clientId !== undefined && clientId > 0,
    })

    return {
      client: query.data,
      refetch: query.refetch,
      fixedPriceDisplay: query.data?.settings?.applyFixedPricing ?? false,
      displayVATPrice: query.data?.applicationTheme?.displayVATPrice ?? false,
      clientThemeName: query.data?.applicationTheme?.name ?? false,
      isLoading,
      error,
      updateClientInvoicing,
      updateClient,
    }
  } catch (error) {
    console.log(error)
    return fallbackResult
  }
}
